import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import {
  CognitoRequestError,
  confirmPasswordReset,
  requestPasswordReset,
} from '../../auth/cognito';
import './auth.css';

const getErrorMessage = (requestError: unknown, fallback: string) => {
  if (
    requestError instanceof CognitoRequestError
    && requestError.code === 'UserNotFoundException'
  ) {
    return 'No member account exists for that email address. Please enroll before resetting a password.';
  }
  if (
    requestError instanceof CognitoRequestError
    && requestError.code === 'InvalidParameterException'
    && requestError.message.includes('no registered/verified email or phone_number')
  ) {
    return 'This account does not have a verified email or phone number yet. Please enroll or ask support to verify the account before resetting the password.';
  }
  return requestError instanceof Error ? requestError.message : fallback;
};

const ResetPassword = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitedEmail = searchParams.get('invite') === '1' ? searchParams.get('email') || '' : '';
  const [email, setEmail] = useState(invitedEmail);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [password, setPassword] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const inviteRequestStarted = useRef(false);

  useEffect(() => {
    if (!invitedEmail || inviteRequestStarted.current) return;
    inviteRequestStarted.current = true;
    setIsSubmitting(true);
    requestPasswordReset(invitedEmail)
      .then(() => {
        setIsConfirming(true);
        setMessage('Your invitation was verified. Check your email for the password confirmation code.');
      })
      .catch((requestError) => {
        setError(getErrorMessage(requestError, 'Unable to send the invitation confirmation code.'));
      })
      .finally(() => setIsSubmitting(false));
  }, [invitedEmail]);

  if (isAuthenticated) {
    return <Navigate to="/profile" replace />;
  }

  const handleRequestReset = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      await requestPasswordReset(email);
      setIsConfirming(true);
      setMessage('Check your email for the password reset code.');
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to send the reset code.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReset = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await confirmPasswordReset(email, confirmationCode, password);
      navigate('/login', {
        replace: true,
        state: { message: 'Your password was reset. You can now log in.' },
      });
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to reset your password.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      await requestPasswordReset(email);
      setMessage('A new password reset code was sent.');
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to resend the reset code.'));
    }
  };

  return (
    <section className="auth-shell">
      <div className="auth-hero">
        <p>Member access</p>
        <h1>Reset your password.</h1>
        <span>Use your account email address to receive a reset code.</span>
      </div>
      <div className="auth-card">
        <form
          className="auth-form"
          onSubmit={isConfirming ? handleConfirmReset : handleRequestReset}
        >
          {error && <p className="auth-message error" role="alert">{error}</p>}
          {message && <p className="auth-message success">{message}</p>}

          <div className="auth-field">
            <label htmlFor="reset-email">Email</label>
            <input
              id="reset-email"
              type="email"
              autoComplete="email"
              required
              disabled={isConfirming}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          {isConfirming && (
            <>
              <div className="auth-field">
                <label htmlFor="reset-code">Reset code</label>
                <input
                  id="reset-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={confirmationCode}
                  onChange={(event) => setConfirmationCode(event.target.value)}
                />
              </div>
              <div className="auth-field">
                <label htmlFor="reset-password">New password</label>
                <input
                  id="reset-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <p className="auth-help">
                  At least 12 characters with uppercase, lowercase, a number, and a symbol.
                </p>
              </div>
            </>
          )}

          <button className="auth-submit" type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? 'Please wait...'
              : isConfirming
                ? 'Reset password'
                : 'Send reset code'}
          </button>
          {isConfirming && (
            <button className="auth-link-button" type="button" onClick={handleResend}>
              Resend reset code
            </button>
          )}
          <p className="auth-switch">
            Remembered it? <Link to="/login">Login</Link>
          </p>
        </form>
      </div>
    </section>
  );
};

export default ResetPassword;
